/**
 * Document → 合成计划（M3-09 / ADR-0005）。
 *
 * 应用层允许同时 import document 与 gpu（两者都在它之下），所以翻译工作
 * 落在这里——gpu/graph 的编译器不认识 `Document`。
 *
 * 遇到 GPU 执行器暂不支持的图层种类（调整/形状/填充/智能对象）或任何启用的
 * 图层样式，直接返回 `null`：调用方（GpuCompositor）据此整帧回退到 CPU
 * 合成桥，而不是渲染出一张缺内容的图（ADR-0005 的纪律）。
 */
import type { Document, GroupLayer, Layer } from '../../document/types';
import type { PlanGroupLayer, PlanLayer } from '../../gpu/graph';
import { maskLayerIdFor } from '../commands/mask';

/** 尝试把文档树翻译成合成计划。任何不支持的内容都让整体失败（返回 null）。 */
export function planFromDocument(
  document: Document,
  options: { readonly skipLayerId?: string } = {},
): PlanGroupLayer | null {
  return planGroup(document.root, options.skipLayerId);
}

function planGroup(group: GroupLayer, skipLayerId: string | undefined): PlanGroupLayer | null {
  const children: PlanLayer[] = [];
  for (const child of group.children) {
    const planned = planLayer(child, skipLayerId);
    if (planned === null) return null;
    children.push(planned);
  }
  return {
    id: group.id as string,
    visible: group.visible,
    opacity: group.opacity,
    fillOpacity: group.fillOpacity,
    blendMode: group.blendMode,
    clipped: group.clipped,
    kind: 'group',
    passThrough: group.passThrough,
    children,
    ...maskOf(group),
  };
}

function planLayer(layer: Layer, skipLayerId: string | undefined): PlanLayer | null {
  // 图层样式：ADR-0005 明确排除，任意种类只要挂了启用的样式就整体回退
  if (layer.effects.some((e) => e.enabled)) return null;

  const visible = layer.visible && layer.id !== skipLayerId;
  const base = {
    id: layer.id as string,
    visible,
    opacity: layer.opacity,
    fillOpacity: layer.fillOpacity,
    blendMode: layer.blendMode,
    clipped: layer.clipped,
    ...maskOf(layer),
  };

  if (layer.kind === 'group') return planGroup(layer, skipLayerId);
  // 文本图层的像素栅格化进普通 tile（M2-18），显示路径与 raster 相同
  if (layer.kind === 'raster' || layer.kind === 'text') return { ...base, kind: 'raster' };
  // 调整/形状/填充/智能对象：GPU 路径暂不支持（ADR-0005），整体回退
  return null;
}

function maskOf(layer: Layer): { readonly mask?: string } {
  return layer.mask?.enabled === true ? { mask: maskLayerIdFor(layer.id) as string } : {};
}
